// src/components/pendenzen/PendenzenQuickList.jsx
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { ClipboardList } from "lucide-react";
import { PendenzenAPI } from "../../constants/api";
import ZuweisungBadge from "../ui/ZuweisungBadge.jsx";
import { useIsMobile } from "../ui/useIsMobile.js";
import { Z_INDEX } from "../ui/zIndex.js";
import { acquireBodyScrollLock } from "../../utils/bodyScrollLock.js";
import { cn } from "../../utils/themeClasses.js";
import { buttonSizes } from "../../design/tokens.js";
import AppButton from "../ui/AppButton.jsx";
export default function PendenzenQuickList({
  open,
  onClose,
  id,
  currentUser
}) {
  const navigate = useNavigate();
    const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const panelRef = useRef(null);
  const lastFocusedRef = useRef(null);
  const [pos, setPos] = useState({
    top: 64,
    left: null,
    right: 16
  });
  const isMobile = useIsMobile();
  const mountedRef = useRef(true);
  const loadReqRef = useRef(0);
  const headerId = useMemo(() => `${id || "pendenzen-panel"}-label`, [id]);
  const myId = Number(currentUser?.id) || null;
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const load = useCallback(async () => {
    const reqId = ++loadReqRef.current;
    setLoading(true);
    try {
      const d = await PendenzenAPI.listMine({
        status: "alle",
        limit: 200
      });
      if (!mountedRef.current || reqId !== loadReqRef.current) return;
      let arr = Array.isArray(d?.items) ? d.items : [];
      if (myId != null) {
        arr = arr.filter(x => {
          const aid = Number(x.assignee_id ?? x.assigned_to ?? x.assignee ?? NaN);
          return aid === myId;
        });
      }
      const openItems = arr.filter(x => x.status === "offen");
      const others = arr.filter(x => x.status !== "offen");
      setItems([...openItems, ...others].slice(0, 10));
    } finally {
      if (mountedRef.current && reqId === loadReqRef.current) setLoading(false);
    }
  }, [myId]);
  useEffect(() => {
    if (open) load();
  }, [open, load]);

  useEffect(() => {
    if (!open || !isMobile) return;
    const release = acquireBodyScrollLock();
    return () => release();
  }, [open, isMobile]);

  // Position unter dem Anker (Desktop)
  useLayoutEffect(() => {
    if (!open || isMobile) return;
    const anchor = document.querySelector("[data-pend-anchor]");
    let top = 64,
      left = null,
      right = 16;
    if (anchor) {
      const r = anchor.getBoundingClientRect();
      top = Math.max(12, r.bottom + 8);
      const vw = window.innerWidth;
      right = Math.max(8, vw - r.right + 4);
      left = null;
    }
    setPos({
      top,
      left,
      right
    });
  }, [open, isMobile]);

  // bei Resize/Scroll leicht neu positionieren
  useEffect(() => {
    if (!open) return;
    let raf = 0;
    const onRecalc = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setPos(p => ({
        ...p
      })));
    };
    window.addEventListener("resize", onRecalc);
    window.addEventListener("scroll", onRecalc, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onRecalc);
      window.removeEventListener("scroll", onRecalc, true);
    };
  }, [open]);

  // Fokus-Handling
  useEffect(() => {
    if (!open) return;
    lastFocusedRef.current = document.activeElement;
    const el = panelRef.current;
    queueMicrotask(() => el?.focus?.());
    const handleKeyDown = e => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
      }
      if (e.key === "Tab") {
        const focusables = el.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (!focusables.length) {
          e.preventDefault();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      lastFocusedRef.current && lastFocusedRef.current.focus?.();
    };
  }, [open, onClose]);
  const goToPendenzen = useCallback(() => {
    onClose?.();
    navigate("/meine-aufgaben?tab=pendenzen");
  }, [navigate, onClose]);
  if (!open) return null;
  const fmtDue = d => {
    if (!d) return "—";
    try {
      const asIso = /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T12:00:00` : d;
      return new Date(asIso).toLocaleDateString("de-CH");
    } catch {
      return "—";
    }
  };
  const isOverdue = d => {
    try {
      if (!d) return false;
      const iso = /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T12:00:00` : d;
      const due = new Date(iso);
      const now = new Date();
      // Nur Datum vergleichen – Uhrzeit egal:
      const normalize = x => new Date(x.getFullYear(), x.getMonth(), x.getDate());
      return normalize(due) < normalize(now);
    } catch {
      return false;
    }
  };
  const statusPill = s => {
    if (s === "offen") {
      return "bg-amber-500/15 text-amber-100 border border-amber-400/25";
    }
    if (s === "erledigt") {
      return "bg-emerald-500/15 text-emerald-100 border border-emerald-400/25";
    }
    return "bg-muted text-white/70 border border-default";
  };
  const Container = <div className="fixed inset-0" style={{
    zIndex: Z_INDEX.modal
  }} onClick={onClose} aria-hidden>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-md" />
      <div id={id} ref={panelRef} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby={headerId} tabIndex={-1} className={cn("outline-none transition-[transform,opacity] duration-200 motion-reduce:transition-none text-white", "backdrop-blur-xl bg-white/[0.03] border border-white/[0.08]", "shadow-[0_32px_80px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.05)]", isMobile ? "fixed left-0 right-0 mx-auto w-full max-w-full rounded-b-none rounded-t-[28px]" : "absolute rounded-[28px] w-[min(26rem,calc(100vw-1.5rem))]")} style={isMobile ? {
      bottom: 0,
      paddingTop: "max(env(safe-area-inset-top,8px),8px)",
      maxHeight: "min(80vh, calc(100vh - (env(safe-area-inset-top,0px)) - 24px))"
    } : {
      top: pos.top,
      right: pos.right ?? undefined,
      left: pos.left ?? undefined
    }}>
        {/* Header */}
        <div className={cn("px-3 py-2.5 flex items-center justify-between gap-2 border-b border-white/[0.08] relative", isMobile ? "pt-6 pb-2.5" : "pt-3")}
        >
          {isMobile && <div className="absolute left-1/2 -translate-x-1/2 top-2 h-1.5 w-10 rounded-full bg-white/20" />}
          <div id={headerId} className="font-semibold text-white flex items-center gap-2 min-w-0 shrink text-sm sm:text-[15px]">
            <span className="flex h-8 w-8 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-lg bg-amber-500/12 border border-amber-400/22" aria-hidden>
              <ClipboardList className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-amber-400" strokeWidth={2.25} />
            </span>
            <span className="truncate min-w-0">Pendenzen</span>
          </div>
          <Link
            to="/meine-aufgaben?tab=pendenzen"
            onClick={onClose}
            className={cn(
              "wrtk-btn inline-flex items-center justify-center gap-1.5 rounded-lg border font-semibold transition-colors duration-150 focus-ring btn-primary no-underline shrink-0",
              buttonSizes.sm,
              "!min-h-0 h-8 px-2.5 text-[11px] leading-tight"
            )}
          >
            Alle Pendenzen
          </Link>
        </div>

        {/* Liste */}
        <div className="px-2 py-2 max-h-[72vh] sm:max-h-[28rem] overflow-y-auto overscroll-contain scrollbar-thin space-y-1.5" role="list" aria-label="Pendenzen Liste">
          {loading && <div className="p-4 text-sm text-white/55">
              Laden…
            </div>}
          {!loading && items.length === 0 && <div className="px-4 py-6 text-sm text-white/55">
              Keine Pendenzen.
            </div>}

          {items.map(it => (
            <AppButton
              key={it.id}
              type="button"
              variant="ghost"
              role="listitem"
              onClick={goToPendenzen}
              className={cn(
                "w-full !justify-start text-left px-3 py-3 transition-colors rounded-xl shadow-none min-h-[44px] h-auto font-normal whitespace-normal items-stretch border",
                it.status === "offen"
                  ? "bg-blue-500/[0.12] border-blue-400/22 hover:bg-blue-500/[0.18]"
                  : "bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.06] hover:border-white/[0.1]"
              )}
            >
              <div className="flex items-start gap-3">
                <span
                  className={cn(
                    "mt-1.5 inline-block w-2 h-2 shrink-0 rounded-full",
                    it.status === "offen" ? "bg-blue-400 shadow-[0_0_8px_rgba(96,165,250,0.55)]" : "bg-white/25"
                  )}
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-white/95 break-words">{it.titel}</div>

                  <div className="mt-1.5 flex flex-wrap gap-1.5 text-[11px]">
                    <span
                      className={cn(
                        "px-1.5 py-0.5 rounded-lg border font-medium",
                        isOverdue(it.faellig_am)
                          ? "bg-red-500/12 text-red-100 border-red-400/22"
                          : "bg-muted text-white/70 border-default"
                      )}
                    >
                      Fällig: {fmtDue(it.faellig_am)}
                    </span>
                    {it.assignee_name && (
                      <ZuweisungBadge name={it.assignee_name} variant="full" size="xs" truncateAt={18} title={it.assignee_name} />
                    )}
                    <span className={cn("px-1.5 py-0.5 rounded-lg font-medium shrink-0", statusPill(it.status))}>{it.status}</span>
                  </div>

                  {it.beschreibung && (
                    <div
                      className="text-xs mt-1.5 text-white/60 leading-snug line-clamp-2 max-w-full min-w-0 [overflow-wrap:anywhere]"
                      title={it.beschreibung.length > 120 ? it.beschreibung : undefined}
                    >
                      {it.beschreibung}
                    </div>
                  )}
                </div>
              </div>
            </AppButton>
          ))}
        </div>

        {isMobile && <div style={{
        height: "max(env(safe-area-inset-bottom, 8px), 8px)"
      }} />}
      </div>
    </div>;
  return createPortal(Container, document.body);
}